import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Target,
  BarChart3,
  Search,
  Lightbulb,
  Shield,
  ChevronLeft,
  CheckCircle2,
  Circle,
  History,
  Lock,
  AlertTriangle,
  Download,
  FileCheck,
  ClipboardCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { LogoIcon } from "../components/Logo";

// Phase Components
import DefinePhase from "../components/phases/DefinePhase";
import MeasurePhase from "../components/phases/MeasurePhase";
import AnalyzePhase from "../components/phases/AnalyzePhase";
import ImprovePhase from "../components/phases/ImprovePhase";
import ControlPhase from "../components/phases/ControlPhase";
import ClosurePhase from "../components/phases/ClosurePhase";
import AuditTrail from "../components/AuditTrail";
import PhaseGate from "../components/PhaseGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phases = [
  { id: "define", label: "Define", icon: Target },
  { id: "measure", label: "Measure", icon: BarChart3 },
  { id: "analyze", label: "Analyze", icon: Search },
  { id: "improve", label: "Improve", icon: Lightbulb },
  { id: "control", label: "Control", icon: Shield },
  { id: "closure", label: "Closure", icon: ClipboardCheck },
];

const phaseOrder = ["define", "measure", "analyze", "improve", "control", "closure", "closed"];

export default function ProjectWorkspace() {
  const { projectId, phase: urlPhase } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(urlPhase || "define");
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [gateKey, setGateKey] = useState(0);

  const fetchProject = useCallback(async () => {
    try {
      const [projectRes, permissionsRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/projects/${projectId}/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!projectRes.ok) throw new Error("Project not found");

      const data = await projectRes.json();
      setProject(data);
      setActivePhase(urlPhase || data.phase || "define");

      if (permissionsRes.ok) {
        const permData = await permissionsRes.json();
        setUserPermissions(permData.permissions || {});
      }
    } catch (error) {
      toast.error(error.message);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [projectId, token, urlPhase, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    if (urlPhase) setActivePhase(urlPhase);
  }, [urlPhase]);

  const handlePhaseChange = (phaseId) => {
    const currentIdx = phaseOrder.indexOf(project?.phase);
    const targetIdx = phaseOrder.indexOf(phaseId);

    // Prevent navigating to future (locked) phases
    if (targetIdx > currentIdx) {
      toast.error(`The ${phaseId.charAt(0).toUpperCase() + phaseId.slice(1)} phase is locked. Complete the current phase gate first.`);
      return;
    }

    setActivePhase(phaseId);
    navigate(`/project/${projectId}/${phaseId}`);
  };

  const onPhaseAdvanced = () => {
    fetchProject();
    setGateKey((k) => k + 1);
  };

  const downloadClosureReport = async () => {
    try {
      toast.loading("Generating closure report...");
      const response = await fetch(`${API}/projects/${projectId}/closure/export-pdf?report_type=full`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to generate closure report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `closure_report_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Closure report downloaded!");
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const getPhaseStatus = (phaseId) => {
    const currentIndex = phaseOrder.indexOf(project?.phase);
    const phaseIndex = phaseOrder.indexOf(phaseId);

    if (phaseIndex < currentIndex) return "completed";
    if (phaseIndex === currentIndex) return "active";
    return "locked";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="project-workspace">
      {/* Sidebar */}
      <aside className="w-72 bg-card border-r border-border flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            <LogoIcon className="w-9 h-9" />
            <span className="font-heading font-bold">BlackBelt AI</span>
          </Link>
        </div>

        {/* Back to Dashboard */}
        <div className="p-3 border-b border-border">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2" data-testid="back-to-dashboard">
              <ChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Project Info */}
        <div className="p-4 border-b border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Project</p>
          <h2 className="font-heading font-semibold truncate" title={project?.title}>
            {project?.title}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 capitalize">
            Phase: <span className="text-foreground font-medium">{project?.phase}</span>
          </p>
        </div>

        {/* Phase Navigation */}
        <nav className="p-4 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">DMAIC Phases</p>
          {phases.map((phase) => {
            const status = getPhaseStatus(phase.id);
            const Icon = phase.icon;
            const isLocked = status === "locked";

            return (
              <button
                key={phase.id}
                onClick={() => handlePhaseChange(phase.id)}
                disabled={isLocked}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-all duration-150 ${
                  activePhase === phase.id
                    ? "bg-primary text-primary-foreground"
                    : status === "completed"
                    ? "text-emerald-400 hover:bg-muted"
                    : isLocked
                    ? "text-muted-foreground/40 cursor-not-allowed"
                    : "hover:bg-muted"
                }`}
                data-testid={`phase-nav-${phase.id}`}
              >
                {status === "completed" ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : isLocked ? (
                  <Lock className="w-4 h-4" />
                ) : status === "active" ? (
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                ) : (
                  <Circle className="w-4 h-4" />
                )}
                <span className="font-medium text-sm">{phase.label}</span>
                {isLocked && <Lock className="w-3 h-3 ml-auto opacity-50" />}
              </button>
            );
          })}
        </nav>

        {/* Gate Panel */}
        {project?.phase !== "closed" && (
          <div className="px-4 pb-4 flex-1 overflow-y-auto border-t border-border pt-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-medium">Phase Gate</p>
            <PhaseGate
              key={gateKey}
              projectId={projectId}
              token={token}
              permissions={userPermissions}
              onPhaseAdvanced={onPhaseAdvanced}
            />
          </div>
        )}

        {/* Project Completed Banner */}
        {project?.phase === "closed" && (
          <div className="px-4 pb-4 flex-1 overflow-y-auto border-t border-border pt-3">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
              <FileCheck className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
              <p className="text-sm font-semibold text-emerald-400 mb-1">Project Closed</p>
              <p className="text-xs text-muted-foreground mb-3">
                Formally closed — read-only mode
              </p>
              <div className="space-y-2">
                <Button
                  onClick={() => handlePhaseChange("closure")}
                  size="sm"
                  variant="outline"
                  className="w-full gap-2"
                  data-testid="view-closure-details-btn"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  View Closure Details
                </Button>
                <Button
                  onClick={downloadClosureReport}
                  size="sm"
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="download-closure-report"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Activity Log */}
        <div className="p-3 border-t border-border mt-auto">
          <Button
            onClick={() => setAuditDialogOpen(true)}
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2"
            data-testid="audit-trail-btn"
          >
            <History className="w-4 h-4" />
            Activity Log
          </Button>
        </div>
      </aside>

      {/* Audit Trail Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white sr-only">Activity Log</DialogTitle>
            <DialogDescription className="sr-only">
              View all project activities and changes
            </DialogDescription>
          </DialogHeader>
          <AuditTrail projectId={projectId} onClose={() => setAuditDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Phase Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {phases.map((phase, i) => {
                const status = getPhaseStatus(phase.id);
                const isLocked = status === "locked";
                return (
                  <div key={phase.id} className="flex items-center">
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activePhase === phase.id
                          ? "bg-primary text-primary-foreground"
                          : status === "completed"
                          ? "text-emerald-400"
                          : isLocked
                          ? "text-muted-foreground/40"
                          : "text-muted-foreground"
                      }`}
                    >
                      {status === "completed" ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : isLocked ? (
                        <Lock className="w-3.5 h-3.5" />
                      ) : (
                        <phase.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                      )}
                      <span className="hidden sm:inline">{phase.label}</span>
                    </div>
                    {i < phases.length - 1 && (
                      <div className={`w-8 h-px mx-2 ${
                        getPhaseStatus(phases[i + 1].id) !== "locked" ? "bg-emerald-400/50" : "bg-border"
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Locked Phase Warning */}
            {getPhaseStatus(activePhase) === "locked" && (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span>This phase is locked</span>
              </div>
            )}
          </div>
        </header>

        {/* Phase Content */}
        <div className="p-8">
          {activePhase === "define" && (
            <DefinePhase projectId={projectId} token={token} />
          )}
          {activePhase === "measure" && (
            <MeasurePhase projectId={projectId} token={token} />
          )}
          {activePhase === "analyze" && (
            <AnalyzePhase projectId={projectId} token={token} />
          )}
          {activePhase === "improve" && (
            <ImprovePhase projectId={projectId} token={token} />
          )}
          {activePhase === "control" && (
            <ControlPhase projectId={projectId} token={token} />
          )}
          {activePhase === "closure" && (
            <ClosurePhase projectId={projectId} token={token} />
          )}
        </div>
      </main>
    </div>
  );
}
